import {
  ActionsGroup,
  Button,
  DialogFooterV2,
  DialogHeaderV2,
  Divider,
  Inset,
  useCloseDialog,
  useSwipeableViews,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { AgentCodeSnippet } from 'components/code';
import { useGetEdgeLocationOffboardingScriptQuery } from 'hooks/queries/omni';

import { CUSTOM_EDGE_PROVIDER } from '../../constants';
import { EnhancedEdgeLocation } from '../../types';

import { CspIcon } from '@cast/shared-csp-icons-ui';

type SetupSlideProps = {
  clusterId: string;
  edgeLocation: EnhancedEdgeLocation;
};

export const SetupSlide = ({ clusterId, edgeLocation }: SetupSlideProps) => {
  const { script, isLoading, error, refetch } =
    useGetEdgeLocationOffboardingScriptQuery({
      clusterId,
      edgeLocationId: edgeLocation.id!,
    });
  const swipeableViewsCtx = useSwipeableViews();
  const closeDialog = useCloseDialog();

  return (
    <Stack data-testid="setup-slide">
      <DialogHeaderV2
        title={
          <Stack direction="row" alignItems="center" gap={8}>
            {edgeLocation.provider === CUSTOM_EDGE_PROVIDER ? null : (
              <CspIcon csp={edgeLocation.provider} size={24} />
            )}
            <Typography variant="h5">Delete edge location</Typography>
            <Divider
              orientation="vertical"
              sx={{ height: 20, alignSelf: 'center' }}
              flexItem
            />
            <Typography variant="P14M" color="textSecondary">
              {edgeLocation.name}
            </Typography>
          </Stack>
        }
      />

      <Inset py pb={0}>
        <Stack gap={24}>
          <Stack gap={4}>
            <Typography variant="A1">
              Open your cloud shell or terminal and run the script
            </Typography>
            <Stack gap={16}>
              <Typography variant="P12R" color="textSecondary">
                Deleting an edge location will remove it from the cluster and
                clean up all associated cloud resources.
              </Typography>
              <Typography variant="P12R" color="textSecondary">
                To delete the edge location, open your cloud shell or terminal
                and run the script below. The script handles offboarding and
                resource cleanup automatically.
              </Typography>
            </Stack>
          </Stack>
          <Stack gap={8}>
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
            >
              Run the script:
            </Typography>
            <Stack
              bgcolor={tokens.theme.bg.structural.surfaceSubtle}
              mx={-24}
              px={24}
              py={20}
            >
              <AgentCodeSnippet
                testId="edge-location-offboarding-script"
                code={script}
                isLoading={isLoading || !script}
                error={error}
                refetch={refetch}
              />
            </Stack>
          </Stack>
        </Stack>
      </Inset>

      <DialogFooterV2
        divider
        endContent={
          <ActionsGroup>
            <Button variant="secondary" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              testId="offboard-edge-location-button"
              variant="primary"
              onClick={() => swipeableViewsCtx?.goToNextSlide()}
            >
              Done
            </Button>
          </ActionsGroup>
        }
      />
    </Stack>
  );
};
